import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiFetch } from "./utils"

export enum CommunicationType {
  STATEMENTS = "statements",
  TRANSACTIONS = "transactions",
  K1S = "k1s",
  QUARTERLY_REPORT = "quarterly_report",
}

export interface Communication {
  id: string
  communication_type: CommunicationType
  household_ids: string[]
  security_exclusion_ids?: string[]
  portfolio_company_exclusion_ids?: string[]
  snapshots?: Snapshot[]
  intro_letter_editorjs_content?: any
  sections?: string[]
  prompt_attribute_tone?: string
  prompt_attribute_goals?: string
  prompt_attribute_general?: string
  prompt_attribute_high_level_summary?: string
  prompt_attribute_attribution_highlights?: string
  prompt_attribute_liquidity_events?: string
  prompt_attribute_new_investments?: string
  prompt_attribute_cash_flows?: string
  financial_statement_signed_id?: string
  body?: string
  footer?: string
  year?: number
  quarter?: number
  documents?: QuarterlyReportDocument[]
}

export interface QuarterlyReportDocument {
  id: string
  document_type: string
  name: string
  filename: string
  file_url?: string
}

export enum ReportSection {
  COVER_LETTER = "Cover Letter",
  TABLE_OF_CONTENTS = "Table of Contents",
  LP_HIGH_LEVEL_KPIS = "LP High Level KPIs & Highlights",
  QPCSP_INTRO_LETTER = "QPCSP Intro Letter",
  LP_INVESTMENT_PERFORMANCE = "LP Investment Performance",
  ATTRIBUTION_EXPOSURE_LETTER = "Attribution & Exposure Letter",
  EXPOSURE_BY_SECTOR = "Exposure by Sector",
  EXPOSURE_BY_GEOGRAPHY = "Exposure by Geography",
  LOGOS_COMPANY_COUNTS = "Logos / Company Counts",
  HISTORICAL_INVESTMENT_PERFORMANCE = "Historical Investment Performance",
  LP_CAPITAL_STATEMENT = "LP Capital Statement",
  QPCSP_FINANCIAL_STATEMENT = "QPCSP Financial Statement",
}

export const ALWAYS_ENABLED_SECTIONS = [
  ReportSection.COVER_LETTER,
  ReportSection.TABLE_OF_CONTENTS,
]

// Toggleable sections (sent to backend)
export const VALID_SECTIONS = [
  ReportSection.LP_HIGH_LEVEL_KPIS,
  ReportSection.QPCSP_INTRO_LETTER,
  ReportSection.LP_INVESTMENT_PERFORMANCE,
  ReportSection.ATTRIBUTION_EXPOSURE_LETTER,
  ReportSection.EXPOSURE_BY_SECTOR,
  ReportSection.EXPOSURE_BY_GEOGRAPHY,
  ReportSection.LOGOS_COMPANY_COUNTS,
  ReportSection.HISTORICAL_INVESTMENT_PERFORMANCE,
  ReportSection.LP_CAPITAL_STATEMENT,
  ReportSection.QPCSP_FINANCIAL_STATEMENT,
]

// All sections in display order
export const ALL_SECTIONS = [...ALWAYS_ENABLED_SECTIONS, ...VALID_SECTIONS]

export interface UpdateCommunicationParams {
  id: string
  body?: string
  footer?: string
  intro_letter_editorjs_content?: any
  household_ids?: string[]
  security_exclusion_ids?: string[]
  portfolio_company_exclusion_ids?: string[]
  sections?: string[]
  prompt_attribute_tone?: string
  prompt_attribute_goals?: string
  prompt_attribute_general?: string
  prompt_attribute_high_level_summary?: string
  prompt_attribute_attribution_highlights?: string
  prompt_attribute_liquidity_events?: string
  prompt_attribute_new_investments?: string
  prompt_attribute_cash_flows?: string
  financial_statement_signed_id?: string
  delete_financial_statement?: boolean
}

export interface GenerateReportsResponse {
  success: boolean
  message: string
  report_count?: number
  errors?: string[]
}

export interface PortfolioCompanyFromSnapshot {
  id: string
  legal_name: string
  display_name?: string
}

export interface Snapshot {
  id: string
  year: number
  quarter: number
  portfolio_companies?: PortfolioCompanyFromSnapshot[]
}

export interface FetchCommunicationOptions {
  includeSnapshot?: boolean
  includePortfolioCompanies?: boolean
  includeQuarterlyReport?: boolean
}

export const fetchCommunication = async (
  id: string,
  options: FetchCommunicationOptions = {},
): Promise<Communication> => {
  const includesParams = []
  let snapshotIncludes = ""

  if (options.includeSnapshot) {
    includesParams.push("snapshots")

    if (options.includePortfolioCompanies) {
      snapshotIncludes = "portfolio_companies"
    }
  }

  const queryParams = new URLSearchParams()
  if (includesParams.length > 0) {
    queryParams.append("includes", includesParams.join(","))
  }
  if (snapshotIncludes) {
    queryParams.append("snapshot_includes", snapshotIncludes)
  }

  const url = `/api/communications/${id}${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`

  return apiFetch<Communication>(url)
}

export const updateCommunication = async (
  params: UpdateCommunicationParams,
) => {
  const requestBody: any = {
    communication: {
      body: params.body,
      footer: params.footer,
      household_ids: params.household_ids,
      security_exclusion_ids: params.security_exclusion_ids,
      portfolio_company_exclusion_ids: params.portfolio_company_exclusion_ids,
    },
  }

  // Add intro_letter_editorjs_content if provided
  if (params.intro_letter_editorjs_content !== undefined) {
    requestBody.communication.intro_letter_editorjs_content =
      params.intro_letter_editorjs_content
  }

  // Add sections if provided
  if (params.sections !== undefined) {
    requestBody.communication.sections = params.sections
  }

  // Add prompt attributes if provided
  if (params.prompt_attribute_tone !== undefined) {
    requestBody.communication.prompt_attribute_tone =
      params.prompt_attribute_tone
  }

  if (params.prompt_attribute_goals !== undefined) {
    requestBody.communication.prompt_attribute_goals =
      params.prompt_attribute_goals
  }

  if (params.prompt_attribute_general !== undefined) {
    requestBody.communication.prompt_attribute_general =
      params.prompt_attribute_general
  }

  if (params.prompt_attribute_high_level_summary !== undefined) {
    requestBody.communication.prompt_attribute_high_level_summary =
      params.prompt_attribute_high_level_summary
  }

  if (params.prompt_attribute_attribution_highlights !== undefined) {
    requestBody.communication.prompt_attribute_attribution_highlights =
      params.prompt_attribute_attribution_highlights
  }

  if (params.prompt_attribute_liquidity_events !== undefined) {
    requestBody.communication.prompt_attribute_liquidity_events =
      params.prompt_attribute_liquidity_events
  }

  if (params.prompt_attribute_new_investments !== undefined) {
    requestBody.communication.prompt_attribute_new_investments =
      params.prompt_attribute_new_investments
  }

  if (params.prompt_attribute_cash_flows !== undefined) {
    requestBody.communication.prompt_attribute_cash_flows =
      params.prompt_attribute_cash_flows
  }

  // Add financial statement signed ID if provided
  if (params.financial_statement_signed_id !== undefined) {
    requestBody.communication.financial_statement_signed_id =
      params.financial_statement_signed_id
  }

  if (params.delete_financial_statement !== undefined) {
    requestBody.communication.delete_financial_statement =
      params.delete_financial_statement
  }

  return apiFetch(`/api/communications/${params.id}`, {
    method: "PATCH",
    body: JSON.stringify(requestBody),
  })
}

export const useCommunication = (
  id: string,
  options: FetchCommunicationOptions = {},
) => {
  return useQuery<Communication, Error>({
    queryKey: ["communication", id, options],
    queryFn: () => fetchCommunication(id, options),
  })
}

export const generateReports = async (
  id: string,
  householdIds?: string[],
): Promise<GenerateReportsResponse> => {
  return apiFetch<GenerateReportsResponse>(
    `/api/communications/${id}/generate_quarterly_reports`,
    {
      method: "POST",
      body: JSON.stringify({
        communication: householdIds ? { household_ids: householdIds } : {},
      }),
    },
  )
}

export const useGenerateReports = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      communicationId,
      householdIds,
    }: {
      communicationId: string
      householdIds?: string[]
    }) => generateReports(communicationId, householdIds),
    onSuccess: (_, variables) => {
      // Invalidate the communication query to refresh data
      queryClient.invalidateQueries({
        queryKey: ["communication", variables.communicationId],
      })
      // Also invalidate the quarterly reports query.
      queryClient.invalidateQueries({
        queryKey: ["household_quarterly_reports", variables.communicationId],
      })
    },
  })
}

export const useUpdateCommunication = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateCommunication,
    onSuccess: (_, variables) => {
      // Invalidate the communication query to refresh data
      queryClient.invalidateQueries({
        queryKey: ["communication", variables.id],
      })
    },
  })
}
